/**
 * Claves de modelo para el filtro de Settings / habitaciones / Favoritos.
 * Shelly usa `tipo` (PLUS1, DIMMER2, …). El resto usa etiquetas estables por marca.
 */

export const MODEL_MANUFACTURERS = {
  SHELLY: 'Shelly',
  SONOS: 'Sonos',
  SAMSUNG: 'Samsung',
  LG: 'LG',
  ANDROID_TV: 'Android TV',
  EVOHOME: 'EvoHome',
  RASPBERRY: 'Raspberry',
} as const;

export type ModelManufacturer = typeof MODEL_MANUFACTURERS[keyof typeof MODEL_MANUFACTURERS];

/** Orden de grupos en el menú de filtros (mismo criterio que el desglose por marca). */
export const MODEL_MANUFACTURER_ORDER: ModelManufacturer[] = [
  MODEL_MANUFACTURERS.SHELLY,
  MODEL_MANUFACTURERS.SONOS,
  MODEL_MANUFACTURERS.SAMSUNG,
  MODEL_MANUFACTURERS.ANDROID_TV,
  MODEL_MANUFACTURERS.LG,
  MODEL_MANUFACTURERS.EVOHOME,
  MODEL_MANUFACTURERS.RASPBERRY,
];

export interface ModelFilterGroup {
  manufacturer: ModelManufacturer;
  models: string[];
}

export type ManufacturerFilterState = 'none' | 'partial' | 'all';

export function shellyModelKey(tipo?: string | null): string | null {
  const t = String(tipo || '').trim();
  return t || null;
}

export function sonosModelKey(modelo?: string | null): string {
  const m = String(modelo || '').trim();
  return m || 'Sonos';
}

export function samsungModelKey(deviceKind?: string | null): string {
  const k = String(deviceKind || '').trim().toLowerCase();
  if (k === 'tv') return 'Samsung TV';
  if (k === 'ac') return 'Samsung AC';
  return 'Samsung';
}

export function lgModelKey(deviceType?: string | null, uiFamily?: string | null): string {
  const dt = String(deviceType || '').trim().toUpperCase();
  const fam = String(uiFamily || '').trim().toLowerCase();
  if (dt.includes('AIR') || fam === 'ac') return 'LG AC';
  if (dt.includes('TV') || fam === 'tv') return 'LG TV';
  if (dt) {
    const pretty = dt.replace(/^DEVICE_/, '').replace(/_/g, ' ');
    return pretty ? `LG ${pretty}` : 'LG';
  }
  return 'LG';
}

export function androidTvModelKey(): string {
  return 'Android TV';
}

export function evohomeModelKey(_modelType?: string | null, _zoneType?: string | null): string {
  return 'EvoHome';
}

/** Tablero físico Raspberry (Pileta / Riego). No es un modelo Shelly. */
export function piletaModelKey(): string {
  return 'Raspberry';
}

export function matchesModelFilter(modelKey: string | null | undefined, filtroModelos: string[]): boolean {
  if (!filtroModelos.length) return true;
  if (!modelKey) return false;
  return filtroModelos.includes(modelKey);
}

/** Filtros que solo aplican a Shelly (no a Sonos/Samsung/LG/…). El de modelos ya es multi-marca. */
export function isShellyExclusiveFilterActive(opts: {
  filtroAuth?: boolean;
  filtroAuthConfigured?: boolean;
  filtroCamera?: boolean;
  filtroEncendido?: boolean;
  filtroApagado?: boolean;
  filtroConConsumo?: boolean;
  filtroSinConsumo?: boolean;
}): boolean {
  return Boolean(
    opts.filtroAuth
    || opts.filtroAuthConfigured
    || opts.filtroCamera
    || opts.filtroEncendido
    || opts.filtroApagado
    || opts.filtroConConsumo
    || opts.filtroSinConsumo,
  );
}

function sortModelKeys(keys: Iterable<string>): string[] {
  return Array.from(keys).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export function collectModelFilterGroups(input: {
  shellyTipos?: Array<string | null | undefined>;
  sonosModelos?: Array<string | null | undefined>;
  samsungKinds?: Array<string | null | undefined>;
  lgDevices?: Array<{ device_type?: string | null; ui_family?: string | null }>;
  androidTvCount?: number;
  evohomeCount?: number;
  piletaCount?: number;
}): ModelFilterGroup[] {
  const byManufacturer = new Map<ModelManufacturer, Set<string>>();
  const add = (manufacturer: ModelManufacturer, key: string) => {
    let set = byManufacturer.get(manufacturer);
    if (!set) {
      set = new Set<string>();
      byManufacturer.set(manufacturer, set);
    }
    set.add(key);
  };

  (input.shellyTipos || []).forEach((t) => {
    const k = shellyModelKey(t);
    if (k) add(MODEL_MANUFACTURERS.SHELLY, k);
  });
  (input.sonosModelos || []).forEach((m) => add(MODEL_MANUFACTURERS.SONOS, sonosModelKey(m)));
  (input.samsungKinds || []).forEach((k) => add(MODEL_MANUFACTURERS.SAMSUNG, samsungModelKey(k)));
  (input.lgDevices || []).forEach((d) => add(MODEL_MANUFACTURERS.LG, lgModelKey(d.device_type, d.ui_family)));
  if ((input.androidTvCount || 0) > 0) add(MODEL_MANUFACTURERS.ANDROID_TV, androidTvModelKey());
  if ((input.evohomeCount || 0) > 0) add(MODEL_MANUFACTURERS.EVOHOME, evohomeModelKey());
  if ((input.piletaCount || 0) > 0) add(MODEL_MANUFACTURERS.RASPBERRY, piletaModelKey());

  return MODEL_MANUFACTURER_ORDER
    .filter((manufacturer) => byManufacturer.has(manufacturer))
    .map((manufacturer) => ({
      manufacturer,
      models: sortModelKeys(byManufacturer.get(manufacturer)!),
    }));
}

export function collectModelFilterKeys(input: {
  shellyTipos?: Array<string | null | undefined>;
  sonosModelos?: Array<string | null | undefined>;
  samsungKinds?: Array<string | null | undefined>;
  lgDevices?: Array<{ device_type?: string | null; ui_family?: string | null }>;
  androidTvCount?: number;
  evohomeCount?: number;
  piletaCount?: number;
}): string[] {
  return collectModelFilterGroups(input).flatMap((group) => group.models);
}

export function manufacturerFilterState(
  selected: string[],
  models: string[],
): ManufacturerFilterState {
  if (!models.length) return 'none';
  const n = models.filter((m) => selected.includes(m)).length;
  if (n === 0) return 'none';
  if (n === models.length) return 'all';
  return 'partial';
}

/** Si todos los modelos de la marca están activos, los quita; si no, los agrega todos. */
export function toggleManufacturerFilter(selected: string[], models: string[]): string[] {
  if (!models.length) return selected;
  if (manufacturerFilterState(selected, models) === 'all') {
    return selected.filter((m) => !models.includes(m));
  }
  const next = new Set(selected);
  models.forEach((m) => next.add(m));
  return Array.from(next);
}
